import React from "react";
import PdfLayout from '../PdfLayout';
import PdfLayoutWidget from './PdfLayoutWidget';
import { Screen, Utils, replaceShortcodesPreservingUnresolved, buildLayoutPreviewShortcodeContext } from '../../services';
import { createStyleString } from '@capsizecss/core';

/**
 * Capsize builds `.${className}`; UUID widget ids often start with a digit,
 * which is an invalid CSS class selector unless escaped (className stays raw).
 */
function css_escape_ident(value) {
    const s = String(value);
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
        return CSS.escape(s);

    return s.replace(/^([0-9])/, digit => `\\3${digit} `);
}

/**
 * Metrics for the painted preview face: Engine `/third/fonts` already lists
 * built-in Nimbus TTFs (Helvetica/Courier/Times) with metrics + URL; Kronos
 * registers them as FontFace under the same names as pdfFontToCssStyle.
 */
function resolve_preview_font_metrics(fonts, style) {
    const family = style?.fontFamily;
    if (!family)
        return null;

    const font = (fonts || []).find(f => f.name === family);
    if (!font)
        return null;

    const variant = style?.fontFamilyVariant;
    if (variant && font.variants?.[variant]?.metrics)
        return font.variants[variant].metrics;

    return font.metrics || null;
}

/** Class representing a PDF layout custom text widget. */
class CustomTextWidget extends PdfLayoutWidget {

    constructor(properties) {
        super({
            ...(CustomTextWidget.getDefaultOptions()),
            ...properties,
        });

        this.type    = PdfLayout.TEXT;
        this.subType = PdfLayout.CUSTOM;
    }

    /**
     * Get this widget default options
     */
    static getDefaultOptions() {
        const textProperties = {};

        return {
            position: { x: 2, y: 2, z: 0 },
            dimensions : { width: 5, height: 0.75 },
            transform: { rotation: 0 },
            options: {
                text: textProperties,
                style: {
                    color: '#000000',
                    fontFamily: "Helvetica",
                    fontSize: 14,
                    lineHeight: 1,
                    textAlign: 'left',
                    textDecoration: 'none',
                    textStrokeWidth: 0,
                    textStrokeColor: '#000000',
                    backgroundColor: '#FFFFFF00',
                    borderStyle: 'solid',
                    borderWidth: 0,
                    borderColor: '#000000',
                    borderRadius: 0,
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    },

                    textTransform: 'none'
                },
            }
        };
    }

    /**
     * Return the list of options this widget handles
     */
    getHandledOptions() {
        return super.getHandledOptions().concat([
            PdfLayout.OPTION_COLOR,
            PdfLayout.OPTION_TEXT,
            PdfLayout.OPTION_FONT_FAMILY,
            PdfLayout.OPTION_FONT_SIZE,
            PdfLayout.OPTION_LINE_HEIGHT,
            PdfLayout.OPTION_TEXT_ALIGNMENT,
            PdfLayout.OPTION_PADDING,
            PdfLayout.OPTION_TEXT_DECORATION,
            PdfLayout.OPTION_TEXT_TRANSFORM,
            PdfLayout.OPTION_TEXT_STROKE_WIDTH,
            PdfLayout.OPTION_TEXT_STROKE_COLOR,
        ]);
    }

    /**
     * Return this widget preview text as string
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {String} lang
     *                 - {Object} setting
     */
    getPreviewText(props) {
        const {
            inEdition, lang, setting, tickettype, pricing, programmation, place,
            edition, editions, layout, selected_edition_id,
        } = props;
        const raw = Utils.localized_or_fallback(this.options?.text, lang, '')
            || (inEdition ? '.....'  : '');

        const context = buildLayoutPreviewShortcodeContext({
            lang,
            setting,
            tickettype,
            pricing,
            programmation,
            place,
            edition,
            editions,
            layout,
            selected_edition_id,
        });
        return replaceShortcodesPreservingUnresolved(raw, { ...context, lang });
    }

    /**
     * Return this widget preview as JSX string
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {String} lang
     */
    getPreview(props) {
        const { fonts } = props;
        const metrics = resolve_preview_font_metrics(fonts, this.options?.style);

        const fontSize = Screen.ppToPx((this.options?.style?.fontSize || 12));
        const lineHeight = this.options?.style?.lineHeight || 1;
        const textStrokeWidth = Screen.ppToPx(Number(this.options?.style?.textStrokeWidth) || 0);
        const textStrokeColor = this.options?.style?.textStrokeColor || '#000000';

        // Capsize with Engine TTF metrics (built-in Helvetica included via FontFace).
        const units_per_em = metrics?.unitsPerEm || 1000;
        const ascent = metrics?.ascent || 1000;
        const cap_height = metrics?.capHeight || 1000;
        const styles = metrics ? createStyleString(css_escape_ident(this.id), {
            fontSize,
            leading: fontSize * lineHeight,
            fontMetrics: {
                capHeight: cap_height,
                ascent,
                descent: Math.abs(metrics.descent || 1000) * -1,
                unitsPerEm: units_per_em,
                lineGap: metrics.lineGap || 0
            },
        }) : '';

        // Capsize flushes caps to the box top. textbox-for-pdfkit places the
        // alphabetic baseline at posY+ascent, so caps sit (ascent−capHeight)
        // below the top. Helvetica ≈ 0.01em (invisible); Roboto ≈ 0.22em.
        const pdf_ascent_pad_px = metrics
            ? Math.max(0, fontSize * (ascent - cap_height) / units_per_em)
            : 0;
        // Under .rnd overflow:hidden, a sub-pixel of antialiasing is clipped
        // at 100% zoom. One CSS px inset keeps « TVA » intact.
        const capsize_bleed_pad_px = styles ? 1 : 0;

        return (
            <div style={{
                fontSize,
                lineHeight,
                // Preserve `\n` in plain preview text (PDF textbox does the same).
                whiteSpace: 'pre-line',
                width: Screen.cmToPx(this.dimensions?.width),
                height: Screen.cmToPx(this.dimensions?.height),
                color: this.options?.style?.color || '#000000',
                textAlign: this.options?.style?.textAlign,
                textTransform: this.options?.style?.textTransform,
                textDecoration: this.options?.style?.textDecoration,
                // Match PDF stroke-underlay then fill (paint stroke first).
                WebkitTextStroke: textStrokeWidth > 0
                    ? `${textStrokeWidth}px ${textStrokeColor}`
                    : undefined,
                paintOrder: textStrokeWidth > 0 ? 'stroke fill' : undefined,
                ...PdfLayout.pdfFontToCssStyle(this.options?.style?.fontFamily, this.options?.style?.fontFamilyVariant),
                backgroundColor: this.options?.style?.backgroundColor,
                border: `${this.options?.style?.borderWidth}px ${this.options?.style?.borderStyle} ${this.options?.style?.borderColor}`,
                borderRadius: `${this.options?.style?.borderRadius || 0}px`,
                boxSizing: 'border-box',
                paddingTop: `${Screen.cmToPx(this.options?.style?.padding?.top || 0) + pdf_ascent_pad_px + capsize_bleed_pad_px}px`,
                paddingRight: `${Screen.cmToPx(this.options?.style?.padding?.right || 0)}px`,
                paddingBottom: `${Screen.cmToPx(this.options?.style?.padding?.bottom || 0)}px`,
                paddingLeft: `${Screen.cmToPx(this.options?.style?.padding?.left || 0)}px`,
            }} >
                { props.interpretHtmlPreview && this.getPreviewText(props) }
                { !props.interpretHtmlPreview && (
                    <div
                        className={this.id}
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{__html: this.getPreviewText(props) }}
                    />
                )}
                {styles ? <style type="text/css">{styles}</style> : null}
            </div>
        );
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'commenting-o';
    }
}

export default CustomTextWidget;
